import type { VehiclePricingCategory } from './parkingPricing';

export type StoredParkingPaymentSession = {
  id: string;
  sessionMode: 'prepaid' | 'start_stop';
  parkingId: string;
  parkingName: string;
  parkingAddress: string | null;
  vehicleId: string;
  vehicleLicensePlate: string;
  vehicleBrand: string;
  vehicleModel: string;
  vehicleType: VehiclePricingCategory;
  pricingTemplateId: string | null;
  pricingTemplateName: string | null;
  chargingPeriodMinutes: number;
  vehicleRate: number;
  startedAt: string;
  stopAt: string | null;
};

const STORAGE_KEY = 'parking-box:payment-session';

const canUseStorage = () => typeof window !== 'undefined' && typeof window.localStorage !== 'undefined';

const normalizeString = (value: unknown): string | null =>
  typeof value === 'string' && value.trim().length > 0 ? value.trim() : null;

const normalizeNullableString = (value: unknown): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  return normalizeString(value);
};

const normalizeNumber = (value: unknown): number | null => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string') {
    const normalized = value.trim().replace(',', '.');
    if (!normalized.length) {
      return null;
    }

    const parsed = Number(normalized);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
};

const normalizeVehicleType = (value: unknown): VehiclePricingCategory | null => {
  if (typeof value !== 'string') {
    return null;
  }

  switch (value.trim()) {
    case 'passenger_cars':
    case 'trucks':
    case 'buses':
    case 'moto_bikes':
    case 'campers':
    case 'charging_station':
      return value.trim() as VehiclePricingCategory;
    default:
      return null;
  }
};

const normalizeDateString = (value: unknown): string | null => {
  const normalized = normalizeString(value);
  if (!normalized) {
    return null;
  }

  const timestamp = Date.parse(normalized);
  return Number.isFinite(timestamp) ? new Date(timestamp).toISOString() : null;
};

const normalizeSessionMode = (value: unknown): 'prepaid' | 'start_stop' | null => {
  if (typeof value !== 'string') {
    return null;
  }

  switch (value.trim()) {
    case 'prepaid':
      return 'prepaid';
    case 'start_stop':
      return 'start_stop';
    default:
      return null;
  }
};

const normalizeSession = (value: unknown): StoredParkingPaymentSession | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null;
  }

  const record = value as Record<string, unknown>;
  const id = normalizeString(record.id ?? record.sessionId ?? record.session_id);
  const stopAt = normalizeDateString(record.stopAt ?? record.stop_at ?? record.endsAt ?? record.ends_at);
  const sessionMode = normalizeSessionMode(record.sessionMode ?? record.session_mode ?? record.mode ?? record.paymentMode) ?? (stopAt ? 'prepaid' : 'start_stop');
  const parkingId = normalizeString(record.parkingId ?? record.parking_id);
  const parkingName = normalizeString(record.parkingName ?? record.parking_name);
  const parkingAddress = normalizeNullableString(record.parkingAddress ?? record.parking_address);
  const vehicleId = normalizeString(record.vehicleId ?? record.vehicle_id);
  const vehicleLicensePlate = normalizeString(record.vehicleLicensePlate ?? record.vehicle_license_plate);
  const vehicleBrand = normalizeString(record.vehicleBrand ?? record.vehicle_brand);
  const vehicleModel = normalizeString(record.vehicleModel ?? record.vehicle_model);
  const vehicleType = normalizeVehicleType(record.vehicleType ?? record.vehicle_type);
  const pricingTemplateId = normalizeNullableString(record.pricingTemplateId ?? record.pricing_template_id);
  const pricingTemplateName = normalizeNullableString(record.pricingTemplateName ?? record.pricing_template_name);
  const chargingPeriodMinutes = normalizeNumber(record.chargingPeriodMinutes ?? record.charging_period_minutes);
  const vehicleRate = normalizeNumber(record.vehicleRate ?? record.vehicle_rate);
  const startedAt = normalizeDateString(record.startedAt ?? record.started_at);

  if (
    !id ||
    !parkingId ||
    !parkingName ||
    !vehicleId ||
    !vehicleLicensePlate ||
    !vehicleBrand ||
    !vehicleModel ||
    !vehicleType ||
    chargingPeriodMinutes === null ||
    chargingPeriodMinutes <= 0 ||
    vehicleRate === null ||
    vehicleRate < 0 ||
    !startedAt
  ) {
    return null;
  }

  if (sessionMode === 'prepaid' && !stopAt) {
    return null;
  }

  return {
    id,
    sessionMode,
    parkingId,
    parkingName,
    parkingAddress,
    vehicleId,
    vehicleLicensePlate,
    vehicleBrand,
    vehicleModel,
    vehicleType,
    pricingTemplateId,
    pricingTemplateName,
    chargingPeriodMinutes,
    vehicleRate,
    startedAt,
    stopAt,
  };
};

const normalizeSessionCollection = (value: unknown): StoredParkingPaymentSession[] => {
  const rawSessions = Array.isArray(value)
    ? value
    : value && typeof value === 'object' && Array.isArray((value as { sessions?: unknown[] }).sessions)
      ? (value as { sessions: unknown[] }).sessions
      : [value];

  const sessionMap = new Map<string, StoredParkingPaymentSession>();

  rawSessions.forEach((item) => {
    const normalizedSession = normalizeSession(item);
    if (!normalizedSession) {
      return;
    }

    sessionMap.set(normalizedSession.id, normalizedSession);
  });

  return Array.from(sessionMap.values());
};

export const readStoredParkingPaymentSession = (): StoredParkingPaymentSession | null => {
  return readStoredParkingPaymentSessions()[0] ?? null;
};

export const readStoredParkingPaymentSessions = (): StoredParkingPaymentSession[] => {
  if (!canUseStorage()) {
    return [];
  }

  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return [];
    }

    return normalizeSessionCollection(JSON.parse(raw));
  } catch {
    return [];
  }
};

export const persistParkingPaymentSession = (session: StoredParkingPaymentSession) => {
  if (!canUseStorage()) {
    return;
  }

  try {
    const nextSessions = [
      ...readStoredParkingPaymentSessions().filter((storedSession) => storedSession.id !== session.id),
      session,
    ];

    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSessions));
  } catch {
    // Ignore storage write failures.
  }
};

export const clearStoredParkingPaymentSession = (sessionId?: string) => {
  if (!canUseStorage()) {
    return;
  }

  try {
    if (sessionId) {
      const nextSessions = readStoredParkingPaymentSessions().filter((storedSession) => storedSession.id !== sessionId);

      if (nextSessions.length > 0) {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(nextSessions));
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }

      return;
    }

    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Ignore storage write failures.
  }
};
